import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import { PollingIntervalGroups } from "apollo/constants";

import {
  MODULES_LIMIT_STORAGE_KEY,
  MODULES_LIMIT,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  FILTER_TYPE,
} from "./constants";
import { SEARCH_MODULES_NEW } from "./gql";

const useModules = () => {
  const [rowsPerPage] = useLocalStorage<number>(MODULES_LIMIT_STORAGE_KEY);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection: INITIAL_SORT_DIRECTION,
    initialSortOption: INITIAL_SORT_OPTION,
  });

  const initialVariables = useMemo(() => {
    return {
      input: {
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
        ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
        first: rowsPerPage || MODULES_LIMIT,
        after: null,
        requestedPage: null,
      },
    };
  }, [
    rowsPerPage,
    filterVariables.predicates,
    filterVariables.searchInput,
    filterVariables.sortOptionFields,
  ]);

  const skipQuery = !filterVariablesLoaded;

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_MODULES_NEW,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
      // APOLLO CLIENT UPDATE
      skip: skipQuery,
    }
  );

  const loadModules = useCallback(
    async ({
      cursor,
      requestedPage,
      rowsPerPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        refetch({
          input: {
            ...initialVariables.input,
            after: cursor,
            first: rowsPerPage,
            requestedPage,
          },
        });
      } catch (error) {
        onError(error);
      }
    },
    [onError, initialVariables, refetch]
  );

  const initialLoading =
    (loading && networkStatus === NetworkStatus.loading && !data?.searchModules) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const modules = useMemo(
    () =>
      data?.searchModules?.edges.map((edge) => edge.node) ||
      previousData?.searchModules?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchModules?.edges, previousData?.searchModules?.edges]
  );

  const paginationResetKey = useMemo(
    () => ({
      searchInput: filterVariables.searchInput,
      predicates: filterVariables.predicates,
      sortOptionFields: filterVariables.sortOptionFields,
    }),
    [filterVariables.searchInput, filterVariables.predicates, filterVariables.sortOptionFields]
  );

  return {
    loadModules,
    error,
    modules,
    initialLoading,
    noResults: !loading && !data?.searchModules,
    endCursor: data?.searchModules?.pageInfo.endCursor,
    startCursor: data?.searchModules?.pageInfo.startCursor,
    isEmpty: data && !modules.length,
    searchIsEmpty: data && !!(filterVariables.searchInput || filterVariables.predicates.length > 0),
    loadingContent,
    paginationResetKey,
  };
};

export default useModules;
